import { useState } from 'react';
import { number, object, string, ZodError } from 'zod';
import CreateAdminComponent from '../../components/Admins/CreateAdmin';
import { useToast } from '../../components/common/ToasterContext';
import getAPIUrl from '../../config';
import { postApiWithAuth } from '../../utils/apiCalls';
import { CreateAdminContainerProps } from './types';
import { trimFormData } from '../../utils/helpers';

const CreateAdmin = ({ createAdmin, setCreateAdmin, roles, setReload }: CreateAdminContainerProps) => {
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [formValues, setFormValues] = useState({
        firstname: '',
        lastname: '',
        roleId: undefined,
        email: '',
        password: '',
    });
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const [showError, setShowError] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const toast = useToast();

    const createAdminValidationSchema = object({
        firstname: string().nonempty({ message: 'Please enter a first name.' }),
        lastname: string().nonempty({ message: 'Please enter a last name.' }),
        roleId: number({ required_error: 'Please select a role.' }),
        email: string()
            .nonempty({ message: 'Please provide an email.' })
            .email({ message: 'Please provide a valid email address' }),
        password: string()
            .nonempty({ message: 'Please enter a password.' })
            .regex(/(?=.*\d)(?=.*[A-Z])(?=.*[@$!%*#?&^_-]).{8,}/, 'Password does not meet the requirements.'),
    });

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            createAdminValidationSchema.parse(trimFormData(formValues));
            await postApiWithAuth(`${getAPIUrl()}/admins/create-admin`, formValues);
            setFormValues({
                firstname: '',
                lastname: '',
                roleId: undefined,
                email: '',
                password: '',
            });
            setSelectedRoleId('');
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            if (setReload) {
                setReload(true);
            }
            setCreateAdmin(false);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
            setShowError(true);
        } finally {
            setSubmitting(false);
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        if (name === 'roleId') {
            setSelectedRoleId(value);
        }
        setFormValues({
            ...formValues,
            [name]: value,
        });
    };

    const handleCloseCreatePop = () => {
        setShowError(false);
        setFormValues({
            firstname: '',
            lastname: '',
            roleId: undefined,
            email: '',
            password: '',
        });
        setCreateAdmin(false);
        setSelectedRoleId('');
    };
    return (
        <CreateAdminComponent
            open={createAdmin}
            handleInput={handleInput}
            roles={roles}
            selectedRoleId={selectedRoleId}
            handleSubmit={onSubmit}
            showError={showError}
            handleCloseCreatePop={handleCloseCreatePop}
            formValues={formValues}
            formErrors={formErrors}
            submitting={submitting}
        />
    );
};
export default CreateAdmin;
